import React from 'react';
import {
  Chart,
  Legend,
  LineSeries,
  ScatterSeries,
  ValueAxis,
  ArgumentAxis,
  Tooltip,
} from '@devexpress/dx-react-chart-material-ui';
import { symbol, symbolCircle } from 'd3-shape';
import { EventTracker, Palette } from '@devexpress/dx-react-chart';
import { TooltipOverlay } from 'components';
import dayjs from 'dayjs';
import { colors } from 'styles/colors';
import {
  IGraphChromatographicDataProps,
  IGraphChromatographicProps,
  IValueLabelComponentProps,
} from './types';
import useStyles from './styles';
import { sortByDate } from './utils';

const GraphChromatographic = (props: IGraphChromatographicProps) => {
  const { data } = props;

  const classes = useStyles();

  const palette = [
    colors.lightBlue,
    colors.statusInProgress,
    colors.statusPaused,
    colors.lateStatus,
    colors.statusCompleted,
    colors.inspectionChecklistReportTopBorder,
  ];

  const Point = (type, styles) => pointProps => {
    const { arg, val, color } = pointProps;
    return (
      <path
        fill={color}
        transform={`translate(${arg} ${val})`}
        d={symbol()
          .size([8 ** 2])
          .type(type)()}
        style={styles}
      />
    );
  };

  const CirclePoint = Point(symbolCircle, {});

  const LineWithPoint = lineProps => (
    <>
      <LineSeries.Path {...lineProps} />
      <ScatterSeries.Path {...lineProps} pointComponent={CirclePoint} />
    </>
  );

  const formatDate = (date: string): string => dayjs(date).format('DD/MM/YY');

  const ValueLabelComponent = ({
    text,
    ...labelProps
  }: IValueLabelComponentProps) => {
    const textString = text as string;
    const textNumber = textString.replace(',', '.');
    const numberAux =
      +textNumber >= 10000
        ? `${+textString.substring(0, textString.indexOf(','))}K`
        : textNumber;
    return <ValueAxis.Label {...labelProps} text={numberAux} />;
  };

  const mapData = React.useMemo(() => {
    const mappedValue: IGraphChromatographicDataProps[] = [];

    data.forEach(value => {
      mappedValue.push({
        h2: Number(value.h2),
        ch4: Number(value.ch4),
        co: Number(value.co),
        c2h4: Number(value.c2h4),
        c2h6: Number(value.c2h6),
        c2h2: Number(value.c2h2),
        collectDate: formatDate(value.collectDate),
      });
    });

    return [...mappedValue].sort(sortByDate('DD/MM/YY', 'ASC'));
  }, [data]);

  return (
    <div className={classes.root}>
      <Chart data={mapData} height={330}>
        <Palette scheme={palette} />
        <Legend position="bottom" />
        <ArgumentAxis />
        <ValueAxis labelComponent={ValueLabelComponent} />
        <LineSeries
          name="H2"
          valueField="h2"
          argumentField="collectDate"
          seriesComponent={LineWithPoint}
        />
        <LineSeries
          name="CH4"
          valueField="ch4"
          argumentField="collectDate"
          seriesComponent={LineWithPoint}
        />
        <LineSeries
          name="CO"
          valueField="co"
          argumentField="collectDate"
          seriesComponent={LineWithPoint}
        />
        <LineSeries
          name="C2H4"
          valueField="c2h4"
          argumentField="collectDate"
          seriesComponent={LineWithPoint}
        />
        <LineSeries
          name="C2H6"
          valueField="c2h6"
          argumentField="collectDate"
          seriesComponent={LineWithPoint}
        />
        <LineSeries
          name="C2H2"
          valueField="c2h2"
          argumentField="collectDate"
          seriesComponent={LineWithPoint}
        />
        <EventTracker />
        <Tooltip overlayComponent={TooltipOverlay} />
      </Chart>
    </div>
  );
};

export default GraphChromatographic;
